/* eslint-disable max-lines-per-function -- App intentionally owns the root provider composition and startup initialization for the application entry point. */
// Initialize root logger at app startup.
import "@next-platform/client-common-core/logger/appLogger";

import {
  IAuthenticatedUser,
  setEntryLocation,
  setExternalUserId,
  useAnalyticsUserTracking,
  useApp,
  useAppDispatch,
  useAppSelector,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { NextUIProvider, Spinner } from "@nextui-org/react";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { ScrollToTop } from "@/components";
import AttributionTracking from "@/components/AttributionTracking";
import CheckoutTokenHandoff from "@/components/CheckoutTokenHandoff/CheckoutTokenHandoff";
import SilentSsoHandoff from "@/components/SilentSsoHandoff/SilentSsoHandoff";
import useApi from "@/components/useApi";
import { APPLICATIONS_ID, ISPARTNER_LOCALSTORAGE_KEY, NSFW_TOGGLE_LOCALSTORAGE_KEY, ROUTES } from "@/constants";
import { Theme } from "@/constants/theme";
import TrackingPixels from "@/features/tracking/TrackingPixels";
import useEnsureUserProfile from "@/features/user/utils/useEnsureUserProfile";
import { useInferenceSettings } from "@/hooks";
import useApplicationRuntimeContextSync from "@/hooks/useApplicationRuntimeContextSync";
import useGoogleAnalyticsInit from "@/hooks/useGoogleAnalyticsInit";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { FeatureFlagProvider } from "@/providers/FeatureFlagProvider";
import MediaProvider from "@/providers/MediaProvider";
import useAuth from "@/services/useAuth";
import { typedLocalStorageService } from "@/utils/localStorageRe";

const applicationThemes = [Theme.LIGHT, Theme.DARK];

// E2E helpers are lazy-loaded so they (and their transitive deps) are split out
// of the main bundle and only fetched when MODE === "e2e".
const E2eAutoLogin = lazy(() => import("@/debug/E2eAutoLogin"));

export default function App() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { pagePath } = useLocalizedLocation();
  const entryLocation = useAppSelector((state) => state.navigation.entryLocation);
  const [query] = useSearchParams();
  const { user, permissions, isLoading: isAuthLoading, isAuthenticationRequired, login } = useAuth();
  const { setPermission, onlyUserId } = useUserInfo();
  const { loadCharactersRatings, getLastTimeFilteredCharacters, setLastTimeFilteredCharacters } = useUserSettings();
  const { jsonApiService } = useApi();
  const { logoutDomain, setUserApplicationId, setApplicationIsPartner } = useApp();
  const { setAuthenticatedUser } = useEnsureUserProfile();
  const jwtQueryParam = "jwt";
  // `no-restricted-syntax` mandates a `state` prefix on every useState
  // binding; `@typescript-eslint/naming-convention` mandates an
  // `is`/`has`/... verb prefix on booleans. For boolean useState the
  // two are mutually exclusive, so each line below disables the
  // boolean verb-prefix rule and keeps the `state` prefix.
  // TODO(karl): [NX-7080] revisit this eslint-disable — reconcile the `state` prefix convention with the boolean verb-prefix rule so one of the two can be relaxed per this case.
  // eslint-disable-next-line @typescript-eslint/naming-convention -- `state` prefix required by `no-restricted-syntax` for useState bindings; boolean verb-prefix rule intentionally waived here.
  const [statePartnerLoggedIn, setStatePartnerLoggedIn] = useState(false);
  const { isAnalyticsInitialized } = useGoogleAnalyticsInit();
  const { hydrateInferenceSettings } = useInferenceSettings();

  useApplicationRuntimeContextSync();

  // Stable references from basicAppConfig — frozen at module load,
  // so referencing them in effect dependency arrays is safe.
  const isPartnerApp = basicAppConfig.isPartnerApp;
  const appId = basicAppConfig.applicationId;
  void typedLocalStorageService.setItem(ISPARTNER_LOCALSTORAGE_KEY, isPartnerApp.toString());
  if (isPartnerApp) {
    void typedLocalStorageService.setItem(NSFW_TOGGLE_LOCALSTORAGE_KEY, "true");
  }
  const isReportPage = pagePath.startsWith(ROUTES.report);
  const isCreatorPage = pagePath.startsWith("/creator/");
  const isChatbotProfilePage = pagePath.startsWith("/chatbot/");
  const isTermsPage = pagePath === "/terms";
  const isPrivacyPolicy = pagePath === "/privacy";
  const isComplaintPolicyPage = pagePath === "/complaint";
  const isRefundPolicy = pagePath === "/refund";
  const isLoginPage = pagePath === ROUTES.login;
  const preventScrollToTop = location.state?.preventScrollToTop; // prevent scrolls to top on navigation if true
  const shouldResetScroll =
    isReportPage || isCreatorPage || isTermsPage || isComplaintPolicyPage || isPrivacyPolicy || isRefundPolicy || isChatbotProfilePage || isLoginPage;

  useEffect(() => {
    setApplicationIsPartner(isPartnerApp);
    // TODO(karl): [NX-7080] revisit this eslint-disable — document / enforce the stability contract of `setApplicationIsPartner` in business-logic so the dep list can include it.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `setApplicationIsPartner` is a stable reference from `useApp()`; adding it would require non-trivial stability guarantees from the business-logic package.
  }, [isPartnerApp]);

  // Dispatch the application as soon as we have it.
  // Required for the refresh token to happen and we won't have any valid auth user to extract it from later.
  useEffect(() => {
    setUserApplicationId(appId);
    jsonApiService.setApplicationId(appId);
    // TODO(karl): [NX-7080] revisit this eslint-disable — either list the stable deps (now that they are module-scoped) or refactor `useApp()` to guarantee stability.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `setUserApplicationId` and `jsonApiService` are stable module-scope references; including them would force unnecessary re-runs without behavioural benefit.
  }, [appId, isPartnerApp]);

  // Partner: external access without token => login
  // Partner: external access WITH token => login with token
  useEffect(() => {
    const queryJwt = query.get(jwtQueryParam);
    if (isPartnerApp && !statePartnerLoggedIn) {
      void login({ isPartner: true, token: queryJwt, app_state: null });
      setStatePartnerLoggedIn(true);
    }
    // TODO(karl): [NX-7080] revisit this eslint-disable — stabilize `login` identity in `useAuth()` so this effect does not have to drop a real dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `login` is an object literal factory from `useAuth()`; each call returns a new reference, which would retrigger this effect every render. `statePartnerLoggedIn` is intentionally omitted to guarantee the partner-login attempt fires exactly once per `query` change.
  }, [isPartnerApp, query]);

  useLayoutEffect(() => {
    if (!shouldResetScroll || preventScrollToTop) {
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    // TODO(karl): [NX-7080] revisit this eslint-disable — move scroll-reset to a dedicated router listener so the effect deps can be accurate.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scroll-to-top must fire on navigation only; adding `shouldResetScroll`/`preventScrollToTop` would also trigger on state-only transitions within the same path.
  }, [pagePath]);

  // Check if user needs to get new auth credentials because they've expired (token and refresh token)
  useEffect(() => {
    if (isPartnerApp && isAuthenticationRequired && logoutDomain) {
      window.open(`https://${logoutDomain}`, "_self");
    }
  }, [isAuthenticationRequired, logoutDomain, isPartnerApp]);

  // dispatch inside useAuth but that's not really where they should live
  useEffect(() => {
    if (user) {
      // TODO(karl): [NX-7080] revisit this eslint-disable — isolate the OAuth wire field into a typed shim so consumers here can use camelCase.
      // eslint-disable-next-line @typescript-eslint/naming-convention -- `external_id` is a Kinde JWT claim name dictated by the OAuth spec; renaming would desynchronize us from the upstream token shape.
      const userToSet = user as IAuthenticatedUser & { external_id?: string };
      setPermission(permissions ?? []);
      setAuthenticatedUser({ ...userToSet });

      if (userToSet.external_id) {
        dispatch(setExternalUserId(userToSet.external_id));
      }
    }
    // TODO(karl): [NX-7080] revisit this eslint-disable — list these setters once their stability is enforced at the hook boundary.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `setPermission` / `setAuthenticatedUser` are stable setters; listing them would only cause extra linter churn, not behavioural benefit.
  }, [user, dispatch, permissions]);

  useAnalyticsUserTracking({ isAnalyticsInitialized });

  // check if user is navigating to a location without the proper credential
  useEffect(() => {
    if (isPartnerApp && !user && !isAuthLoading && (pagePath === "/" || pagePath === "/external") && logoutDomain) {
      window.open(`https://${logoutDomain}`, "_self");
    }
  }, [isPartnerApp, user, isAuthLoading, pagePath, logoutDomain]);
  useEffect(() => {
    // setting initial entry location of application
    if (!entryLocation) {
      dispatch(setEntryLocation(pagePath));
    }
  }, [entryLocation, pagePath, dispatch]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }
    hydrateInferenceSettings();
    if (onlyUserId) {
      void loadCharactersRatings();
    }
    const hydrateLastTimeFilteredCharacters = async () => {
      const lastTimeFilteredCharacters = await getLastTimeFilteredCharacters();
      if (lastTimeFilteredCharacters) {
        setLastTimeFilteredCharacters(lastTimeFilteredCharacters);
      }
    };
    void hydrateLastTimeFilteredCharacters();
    // TODO(karl): [NX-7080] revisit this eslint-disable — stabilize the hydration helpers at their hook boundary so the dep list can be exhaustive.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- these hydration helpers are stable Redux Toolkit selectors/actions bound in `useUserSettings()` / `useInferenceSettings()`; they must run exactly on auth-state transitions, not on their own identity changes.
  }, [onlyUserId, isAuthLoading]);

  return (
    <NextUIProvider navigate={navigate} validationBehavior="aria">
      <NextThemesProvider attribute="class" themes={applicationThemes} defaultTheme={appId === APPLICATIONS_ID.PixelChat ? Theme.LIGHT : Theme.DARK}>
        <FeatureFlagProvider>
          <AttributionTracking />
          <TrackingPixels />
          <SilentSsoHandoff />
          <CheckoutTokenHandoff />
          {import.meta.env.MODE === "e2e" ? (
            <Suspense fallback={null}>
              <E2eAutoLogin />
            </Suspense>
          ) : null}
          <MediaProvider>
            <ScrollToTop>
              <Suspense fallback={<Spinner />}>
                <Outlet />
              </Suspense>
            </ScrollToTop>
          </MediaProvider>
        </FeatureFlagProvider>
      </NextThemesProvider>
    </NextUIProvider>
  );
}
